import { FC, ReactNode } from "react";
import { Dayjs } from "dayjs";
import { MdStar } from "react-icons/md";
import { DataType } from "src/constants";
import { getValue, handlers } from "src/utility";
import { compactNumber } from "src/utility";
import { SorterLabel } from "src/components/SortersBar/SorterButton";

export function computeHotelLabels(group: Map<string | number, any[]>, sorter: any, pointsFactor: number) {
  const keys = Array.from(group.keys());
  const handler = handlers[sorter.category as DataType];
  const parsed = keys.map((k) => handler(k as any)).filter((v) => v != null);

  let min: any = null;
  let max: any = null;

  if (sorter.category === "number") {
    const nums = parsed.filter((n) => !Number.isNaN(n)) as number[];
    if (nums.length) {
      min = Math.min(...nums);
      max = Math.max(...nums);
    }
  }

  if (sorter.category === "date") {
    const dates = parsed as Dayjs[];
    if (dates.length) {
      min = dates.reduce((a, b) => (b.isBefore(a) ? b : a));
      max = dates.reduce((a, b) => (b.isAfter(a) ? b : a));
    }
  }

  const minHotel = group.get(min)?.[0];
  const maxHotel = group.get(max)?.[0];

  return {
    minLabel: minHotel
      ? {
          price: `${compactNumber(getValue(minHotel, ["minPrice"]))} ${getValue(minHotel, ["currency"])}`,
          starRating: getValue(minHotel, ["hotelRating"]),
          priceInPoints: `${compactNumber(getValue(minHotel, ["rooms", 0, "totalFare"]) * pointsFactor)} Pts`,
        }
      : null,
    maxLabel: maxHotel
      ? {
          price: `${compactNumber(getValue(maxHotel, ["minPrice"]))} ${getValue(maxHotel, ["currency"])}`,
          starRating: getValue(maxHotel, ["hotelRating"]),
          priceInPoints: `${compactNumber(getValue(maxHotel, ["rooms", 0, "totalFare"]))} Pts`,
        }
      : null,
  };
}

export const DefaultLabel: FC<{ label: SorterLabel | null }> = ({ label }) => {
  if (!label) return null;
  return (
    <div className="flex w-full items-center gap-0.5 text-[clamp(.7rem,1cqw,1rem)] text-font-subtle">
      <span>{label.price}</span>
      <span>. {label.priceInPoints} .</span>
      <span className="flex items-center">
        <MdStar className="text-icon-rating-star" /> {label.starRating}
      </span>
    </div>
  );
};

export const defaultRenderLabel = (label: SorterLabel | null): ReactNode => <DefaultLabel label={label} />;

export function getActiveSorterLabel(
  dataGroups: Map<string, Map<number | string, any[]>>,
  sorter: any,
  value: boolean,
  pointsFactor: number,
): ReactNode {
  const group = sorter && dataGroups.get(sorter.key);
  if (!sorter || !group) return null;

  const { minLabel, maxLabel } = computeHotelLabels(group, sorter, pointsFactor);
  return defaultRenderLabel(value ? maxLabel : minLabel);
}
